import React, { useState } from 'react';
import { ArrowLeft, Edit2, Trash2, Calendar, Key, Power, PowerOff, CheckCircle2, FileText, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { AdminService } from '../../services/api';
import ResetPasswordModal from '../ResetPasswordModal';

const DoctorDetails = ({ selectedDoctor, clinics, onBack, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const userRole = Cookies.get('role');
  const [currentStatus, setCurrentStatus] = useState(selectedDoctor.vendor_approve_status || 'A');
  const [statusLoading, setStatusLoading] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');

  const isActive = currentStatus === 'A';

  const handleToggleStatus = async () => {
    const nextStatus = isActive ? 'D' : 'A';
    const actionLabel = isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${actionLabel} this doctor?`)) return;

    setStatusLoading(true);
    try {
      const response = await AdminService.setVendorStatus(selectedDoctor.id, nextStatus);
      if (response && response.status === 1) {
        setCurrentStatus(nextStatus);
        selectedDoctor.vendor_approve_status = nextStatus;
        setAlertMsg(`Doctor ${isActive ? 'deactivated' : 'activated'} successfully!`);
        setTimeout(() => setAlertMsg(''), 4000);
      } else {
        alert(response?.msg || 'Failed to update doctor status');
      }
    } catch (err) {
      alert(err.message || 'Error updating status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDoctorClick = (doctor) => {
    navigate(`/doctor-appointments/${doctor.id}/${selectedDoctor.clinic_id}`);
  };

  return (
    <div className="detail-view">
      {alertMsg && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-2">
          <CheckCircle2 size={16} />
          <span>{alertMsg}</span>
        </div>
      )}
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={16} /> Back to Doctors
        </button>
        <div className="flex items-center gap-3">
          <h2>{selectedDoctor.vendorName || 'Unnamed Doctor'}</h2>
          <span className={`status-pill ${isActive ? 'approved' : 'pending'}`}>
            <span className="dot" />
            {isActive ? 'Approved / Active' : 'Deactivated'}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            className="create-btn flex items-center gap-1"
            onClick={() => handleDoctorClick(selectedDoctor)}
          >
            <Calendar size={16} /> View Appointments
          </button>
          {userRole === 'SuperAdmin' && (
            <>
              <button
                className={`create-btn flex items-center gap-1 ${
                  isActive ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                onClick={handleToggleStatus}
                disabled={statusLoading}
              >
                {isActive ? <PowerOff size={16} /> : <Power size={16} />}
                {statusLoading ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button
                className="create-btn flex items-center gap-1 bg-slate-700 hover:bg-slate-800"
                onClick={() => setIsResetModalOpen(true)}
              >
                <Key size={16} /> Reset Password
              </button>
              <button className="create-btn flex items-center gap-1" onClick={onEdit}>
                <Edit2 size={16} /> Edit
              </button>
              <button className="create-btn flex items-center gap-1 bg-red-500 hover:bg-red-600" onClick={onDelete}>
                <Trash2 size={16} /> Delete
              </button>
            </>
          )}
        </div>
      </div>
      <ResetPasswordModal
        vendor={selectedDoctor}
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onSuccess={() => setAlertMsg('Password reset successfully')}
      />
      <div className="detail-content">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Profile Photo:</span>
            {selectedDoctor.vendor_profile_path ? (
              <img
                src={selectedDoctor.vendor_profile_path}
                alt={selectedDoctor.vendorName || 'Doctor Image'}
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <span>No Image</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span>{selectedDoctor.vendorEmail || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Contact Number:</span>
            <span>{selectedDoctor.vendorConNumber || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Specialization:</span>
            <span>{selectedDoctor.specialty || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Education:</span>
            <span>{selectedDoctor.education || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Experience:</span>
            <span>{selectedDoctor.experience || selectedDoctor.vendor_experience ? `${selectedDoctor.experience || selectedDoctor.vendor_experience}` : 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Clinics:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {Array.isArray(selectedDoctor.clinic_list) && selectedDoctor.clinic_list.length > 0 ? (
                selectedDoctor.clinic_list.map((c, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                  >
                    {c.clinic || `Clinic ${c.clinicId}`}
                  </span>
                ))
              ) : selectedDoctor.clinic && selectedDoctor.clinic.includes(',') ? (
                selectedDoctor.clinic.split(',').map((name, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                  >
                    {name.trim()}
                  </span>
                ))
              ) : (
                <span>{selectedDoctor.clinic || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="info-item">
            <span className="info-label">Registration Number:</span>
            <span>{selectedDoctor.vendor_regNumber || selectedDoctor.regNumber || selectedDoctor.reg_no || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Gender:</span>
            <span>{selectedDoctor.gender || selectedDoctor.vendor_contact_gender || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Location:</span>
            <span>{selectedDoctor.location || selectedDoctor.vendor_location || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Address:</span>
            <span>{selectedDoctor.address || selectedDoctor.vendor_address || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Languages Spoken:</span>
            <span>{selectedDoctor.languages || 'N/A'}</span>
          </div>
          <div className="info-item col-span-full">
            <span className="info-label">Doctor Commitment (Assured Appointment):</span>
            <div className="mt-1">
              {selectedDoctor.is_committed === 1 || selectedDoctor.is_committed === '1' || selectedDoctor.commitment_type === 'committed' ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-600 text-white shadow-xs">
                      <ShieldCheck size={14} />
                      Committed Doctor – Assured Appointment
                    </span>
                    <span className="text-xs text-emerald-800 font-semibold">
                      Preferred Doctor Badge Active
                    </span>
                  </div>
                  <p className="text-xs text-emerald-900 m-0 mb-2 leading-relaxed">
                    Doctor voluntarily agrees to appointment timing and waiting-time commitments with advance payment protection.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-emerald-800 font-medium">
                    <div className="flex items-center gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Advance Payment:</strong> Prepayment required to confirm slot.</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Automatic Refund:</strong> Issued if commitment is not met.</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Patient Badge:</strong> Assured Doctor badge displayed.</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-emerald-600 font-bold">✓</span>
                      <span><strong>Search Priority:</strong> Priority visibility in doctor search.</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-xs">
                  <span className="font-semibold">Normal Doctor</span>
                  <span className="text-slate-400">— Regular appointment booking without waiting-time commitments</span>
                </div>
              )}
            </div>
          </div>
          <div className="info-item col-span-full">
            <span className="info-label">Conditions Treated:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {Array.isArray(selectedDoctor.conditions) && selectedDoctor.conditions.length > 0 ? (
                selectedDoctor.conditions.map((cond, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-800 border border-teal-200 shadow-2xs"
                  >
                    {cond}
                  </span>
                ))
              ) : (
                <span className="text-slate-400 italic">None added</span>
              )}
            </div>
          </div>
          <div className="info-item col-span-full">
            <span className="info-label">Procedures Performed:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {Array.isArray(selectedDoctor.procedures) && selectedDoctor.procedures.length > 0 ? (
                selectedDoctor.procedures.map((proc, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200 shadow-2xs"
                  >
                    {proc}
                  </span>
                ))
              ) : (
                <span className="text-slate-400 italic">None added</span>
              )}
            </div>
          </div>
          <div className="info-item">
            <span className="info-label">Medical License:</span>
            {selectedDoctor.license || selectedDoctor.licencePath ? (
              <a
                href={selectedDoctor.license || selectedDoctor.licencePath}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 underline"
              >
                <FileText size={14} /> View License
              </a>
            ) : (
              <span>Not Provided</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label">Authorized Signature:</span>
            {selectedDoctor.authorizedSignature || selectedDoctor.authorizedSign ? (
              <a
                href={selectedDoctor.authorizedSignature || selectedDoctor.authorizedSign}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 underline"
              >
                <FileText size={14} /> View Signature
              </a>
            ) : (
              <span>Not Provided</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label">Commercial License:</span>
            {selectedDoctor.commercialLicense || selectedDoctor.commercialLicence ? (
              <a
                href={selectedDoctor.commercialLicense || selectedDoctor.commercialLicence}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 underline"
              >
                <FileText size={14} /> View License
              </a>
            ) : (
              <span>Not Provided</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label">Civil ID:</span>
            {selectedDoctor.civilId || selectedDoctor.civilIdPath ? (
              <a
                href={selectedDoctor.civilId || selectedDoctor.civilIdPath}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 underline"
              >
                <FileText size={14} /> View Civil ID
              </a>
            ) : (
              <span>Not Provided</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label">Status:</span>
            <span className={`status ${(selectedDoctor.vendor_approve_status || '').toLowerCase()}`}>
              {selectedDoctor.vendor_approve_status === 'A' ? 'Approved' : selectedDoctor.vendor_approve_status || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDetails;